import React from "react";
import { Button } from "@/components/ui/button";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { BudgetLineRow } from "./BudgetLineRow";
import { BudgetSummary } from "./BudgetSummary";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import { SrIcon } from "@/components/ui/SrIcon";
import type { BudgetLine } from "@/hooks/use-new-project-form";

interface StepBudgetProps {
  budgetLines: BudgetLine[];
  aiParsedValues: Record<string, { amount: number }>;
  onUpdateBudgetLine: (line: BudgetLine) => void;
  onDeleteBudgetLine: (id: string) => void;
  onAddBudgetLine: (phase: string) => void;
  budgetTotal: number;
  totalEstCost: number;
  totalHours: number;
  calculatedTotal: number;
  validationDiff: number;
  offerOverheadPct: number;
  offerDiscountPct: number;
  offerDiscountAmount: number;
}

export function StepBudget({
  budgetLines,
  aiParsedValues,
  onUpdateBudgetLine,
  onDeleteBudgetLine,
  onAddBudgetLine,
  budgetTotal,
  totalEstCost,
  totalHours,
  calculatedTotal,
  validationDiff,
  offerOverheadPct,
  offerDiscountPct,
  offerDiscountAmount,
}: StepBudgetProps) {
  const { data: templates = [] } = useOfferLineTemplates();
  const unmatchedCount = budgetLines.filter((l) => !l.template_id).length;
  return (
    <div className="space-y-6">
      {unmatchedCount > 0 && (
        <div className="px-3 py-2 rounded-md border-[1.5px] border-foreground bg-[#FEF3C7] text-[12px] text-[#78350F]">
          {unmatchedCount} av {budgetLines.length} linjer matchet ikke en mal.
          Klikk på "Velg mal…" under linjen for å koble til riktig produkt
          så fasen blir riktig og linjene gjenkjennes i Timetildeling.
        </div>
      )}
      {(Object.keys(PHASE_LABELS) as ProjectPhase[]).map(phase => {
        const phaseLines = budgetLines.filter(l => l.phase === phase);
        if (phaseLines.length === 0) return null;
        return (
          <div key={phase}>
            <h3 className="font-medium text-sm mb-2">{PHASE_LABELS[phase]}</h3>
            <table className="w-full text-sm mb-2">
              <thead>
                <tr className="border-b [&>th]:py-2 [&>th]:px-2 [&>th]:font-medium [&>th]:text-muted-foreground [&>th]:text-xs">
                  <th className="text-left">Beskrivelse</th>
                  <th className="text-right w-20">Timer</th>
                  <th className="text-right w-28">Beløp</th>
                  <th className="text-right w-28">Est. ekst. kost</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {phaseLines.map(line => (
                  <BudgetLineRow
                    key={line.id}
                    line={line}
                    templates={templates}
                    aiParsedValues={aiParsedValues}
                    onUpdate={onUpdateBudgetLine}
                    onDelete={() => onDeleteBudgetLine(line.id)}
                  />
                ))}
              </tbody>
            </table>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAddBudgetLine(phase)}
            >
              <SrIcon name="plus" className="w-3 h-3 mr-1"  />
              Ny linje
            </Button>
          </div>
        );
      })}

      {budgetLines.length > 0 && (
        <BudgetSummary
          budgetTotal={budgetTotal}
          totalEstCost={totalEstCost}
          totalHours={totalHours}
          calculatedTotal={calculatedTotal}
          validationDiff={validationDiff}
          offerOverheadPct={offerOverheadPct}
          offerDiscountPct={offerDiscountPct}
          offerDiscountAmount={offerDiscountAmount}
        />
      )}
    </div>
  );
}
